import { useState, useCallback, useEffect, useRef } from "react";
import { Lock, Wrench, Clock, Layers, User, MessageCircle, Map, Sparkles } from "lucide-react";
import { IN_DEV_COURSES } from "../data/inDevCourses";
import { useNavigate, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import { useCourses } from "../hooks/useCourses";
import { QUERY_LOADING_STAGE_LABEL, useQueryLoadingStage } from "../hooks/useQueryLoadingStage";
import { useResize } from "../hooks/useResize";
import { SEOHead } from "../components/SEOHead";
import { generateBreadcrumbSchema } from "../utils/seo";
import { API_HOST } from "../constants";
import {
  AI_TUTOR_MIN_WIDTH,
  AI_TUTOR_MAX_WIDTH,
  AI_TUTOR_DEFAULT_WIDTH,
  AI_TUTOR_ASIDE_WIDTH_KEY,
  AI_TUTOR_DESKTOP_TOP,
} from "../constants/aiTutorLayout";
import { LAYERS } from "../constants/layers";
import { AITutorDualModePanel, type AITutorDualMode } from "../components/AITutorDualModePanel";
import { Loading } from "../components/common/Loading";
import type { TelegramUser } from "../types";

// ─── Course Card ──────────────────────────────────────────────────────────────

interface CourseCardProps {
  title: string;
  description?: string;
  backgroundPattern: string;
  isFeatured?: boolean;
  isDisabled?: boolean;
  isInDev?: boolean;
  isFree?: boolean;
  price?: string;
  coverImage?: string;
  durationHours?: number;
  modulesCount?: number;
  targetLevel?: string;
  onClick?: () => void;
}

const CourseCard = ({
  title,
  description,
  backgroundPattern: _backgroundPattern,
  isFeatured = false,
  isDisabled = false,
  isInDev = false,
  isFree = false,
  price,
  coverImage,
  durationHours,
  modulesCount,
  targetLevel,
  onClick,
}: CourseCardProps) => {
  const coverSrc = coverImage
    ? coverImage.startsWith("http")
      ? coverImage
      : `${API_HOST}${coverImage}`
    : null;

  // До md: квадратная обложка по ширине карточки; с md — фиксированная высота как на десктопе
  const coverClassName =
    "relative w-full overflow-hidden flex-shrink-0 group/cover aspect-square md:aspect-auto md:h-44";

  const fillGridCell = !isFeatured;

  return (
    <div
      className={`relative ${fillGridCell ? "h-full min-h-0" : "h-auto sm:h-full"} ${
        isDisabled ? "cursor-not-allowed" : "cursor-pointer group"
      }`}
      onClick={isDisabled ? undefined : onClick}
    >
      <div
        className={`relative bg-white rounded-2xl overflow-hidden ${
          fillGridCell ? "h-full min-h-0" : "h-auto sm:h-full"
        } flex flex-col transition-all duration-200 ${
          isDisabled
            ? "border border-neutral-border/60 opacity-75"
            : isFeatured
            ? "border-2 border-primary/30 shadow-md hover:shadow-xl hover:border-primary/60"
            : "border border-neutral-border shadow-md hover:shadow-xl hover:border-primary/30"
        }`}
      >
        {/* Cover */}
        <div className={coverClassName}>
          {coverSrc ? (
            <img
              src={coverSrc}
              alt={title}
              className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-[1.03]"
            />
          ) : (
            <div
              className="w-full h-full"
              style={{
                backgroundColor: "#ffffff",
                backgroundImage: `linear-gradient(rgba(34,197,94,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(34,197,94,0.2) 1px, transparent 1px)`,
                backgroundSize: "20px 20px",
              }}
            />
          )}

          {/* Blur overlay + lock for disabled */}
          {isDisabled && (
            <div className="absolute inset-0 backdrop-blur-[3px] bg-black/30 flex items-center justify-center">
              <Lock className="w-9 h-9 text-white/60 drop-shadow-lg" />
            </div>
          )}

          {/* Top-left: Free badge */}
          {isFree && !isDisabled && (
            <span className="absolute top-2.5 left-2.5 inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold bg-emerald-500 text-white shadow-sm">
              Бесплатно
            </span>
          )}

          {/* Bottom-left: level badge */}
          {targetLevel && (
            <span className="absolute bottom-2.5 left-2.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-black/50 text-white backdrop-blur-sm shadow-sm">
              <User className="h-3 w-3" />
              {targetLevel}
            </span>
          )}

          {/* Bottom-left (below level): duration badge on cover */}
          {!isFeatured && typeof durationHours === "number" && durationHours > 0 && (
            <span className="absolute top-2.5 right-2.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium bg-black/50 text-white backdrop-blur-sm shadow-sm">
              <Clock className="h-3 w-3" />
              {durationHours} ч
            </span>
          )}

          {/* Bottom-right: "В разработке" badge */}
          {(isDisabled || isInDev) && (
            <span className="absolute bottom-2.5 right-2.5 inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-semibold bg-orange-500 text-white shadow-sm">
              <Wrench className="h-3 w-3" />
              В разработке
            </span>
          )}

          {/* Description overlay on hover (non-featured only) */}
          {description && !isFeatured && (
            <div className="absolute inset-x-0 bottom-0 bg-black/60 backdrop-blur-sm px-3 py-2.5 translate-y-full group-hover:translate-y-0 transition-transform duration-200">
              <p className="text-white/90 text-[11px] leading-relaxed line-clamp-3">{description}</p>
            </div>
          )}
        </div>

        {/* Content: в сетке курсов — flex-1 + прижатый вниз футер для одинаковой высоты строки */}
        <div
          className={`px-3 py-2 flex flex-col ${fillGridCell ? "flex-1 min-h-0" : ""}`}
        >
          <div className={fillGridCell ? "min-h-[2.75rem]" : undefined}>
            <h3
              className={`font-semibold text-neutral-text mb-0.5 ${
                isFeatured ? "text-base line-clamp-2" : "text-sm line-clamp-2"
              }`}
            >
              {title}
            </h3>
            {description && isFeatured && (
              <p className="text-neutral-text/60 text-xs leading-relaxed line-clamp-2 sm:line-clamp-3">
                {description}
              </p>
            )}
          </div>

          <div
            className={`flex items-center justify-between gap-2 flex-wrap ${
              fillGridCell ? "mt-auto pt-1.5" : "mt-2"
            }`}
          >
            <div className="flex flex-wrap gap-1.5">
              {typeof modulesCount === "number" && modulesCount > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium bg-indigo-500/10 text-indigo-700 ring-1 ring-indigo-500/30">
                  <Layers className="h-3 w-3" />
                  {modulesCount} модулей
                </span>
              )}
            </div>
            {price && !isFree && (
              <span className="text-[10px] text-neutral-text/45 whitespace-nowrap">{price}</span>
            )}
            {isFeatured && !isDisabled && (
              <span className="text-[11px] font-semibold text-primary group-hover:underline">
                Начать →
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Mobile AI Tutor Modal ────────────────────────────────────────────────────

interface MobileTutorModalProps {
  user: TelegramUser | null;
  onClose: () => void;
  initialMode?: AITutorDualMode;
}

function MobileTutorModal({ user, onClose, initialMode = "chat" }: MobileTutorModalProps) {
  const [mode, setMode] = useState<AITutorDualMode>(initialMode);

  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-white flex flex-col">
      <AITutorDualModePanel
        user={user}
        onClose={onClose}
        embedded
        mode={mode}
        onModeChange={setMode}
        showCloseButton
        chatProps={{
          contextType: "generic",
          enableFeedback: true,
          embedded: true,
          headerSubtitle: "Вопросы по курсам и обучению.",
          showChatHistoryInEmbedded: true,
          containerClassName: "flex-1 min-h-0 flex flex-col overflow-hidden border-0 shadow-none rounded-none",
        }}
        roadmapProps={{ isMobile: true }}
      />
    </div>,
    document.body
  );
}

// ─── Main Courses Page ────────────────────────────────────────────────────────

interface CoursesProps {
  user: TelegramUser | null;
}

export const Courses = ({ user }: CoursesProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { courses, loading, error } = useCourses();
  const queryLoadingPhase = useQueryLoadingStage(loading);
  const [mobileModalOpen, setMobileModalOpen] = useState(false);
  const [mobileModalInitialMode, setMobileModalInitialMode] = useState<AITutorDualMode>("chat");
  const [tutorAsideMode, setTutorAsideMode] = useState<AITutorDualMode>("chat");
  const [chatPanelMinWidth, setChatPanelMinWidth] = useState(380);
  const [showAITutor, setShowAITutor] = useState(true);

  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth >= 1024 : true
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // Auto-open mobile roadmap modal when navigating from quiz modal
  useEffect(() => {
    if ((location.state as any)?.showRoadmap && !isDesktop) {
      setMobileModalInitialMode("roadmap");
      setMobileModalOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, isDesktop, navigate, location.pathname]);

  const savedWidth = parseInt(localStorage.getItem(AI_TUTOR_ASIDE_WIDTH_KEY) ?? "", 10);
  const initialWidth = isNaN(savedWidth) ? AI_TUTOR_DEFAULT_WIDTH : savedWidth;

  const aiTutorResize = useResize({
    initialSize: initialWidth,
    minSize: Math.max(AI_TUTOR_MIN_WIDTH, chatPanelMinWidth),
    maxSize: AI_TUTOR_MAX_WIDTH,
    direction: "horizontal",
    invertHorizontal: true,
    onSizeChange: useCallback((w: number) => localStorage.setItem(AI_TUTOR_ASIDE_WIDTH_KEY, String(w)), []),
  });

  // Keep paddingRight in sync with sidebar width during drag (no re-renders)
  const contentRef = useRef<HTMLDivElement>(null);
  const resizeHandleRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const aside = aiTutorResize.elementRef.current;
    if (!aside) return;
    const observer = new ResizeObserver(([entry]) => {
      const w = entry.contentRect.width;
      if (contentRef.current && isDesktop && showAITutor) {
        contentRef.current.style.paddingRight = `calc(1.5rem + ${w}px)`;
      }
      if (resizeHandleRef.current) {
        resizeHandleRef.current.style.right = `${w}px`;
      }
    });
    observer.observe(aside);
    return () => observer.disconnect();
  }, [aiTutorResize.elementRef, isDesktop, showAITutor]);

  const basicCourse = courses.find((c) => c.slug === "clickhouse-basics");
  const otherCourses = courses.filter((c) => c.slug !== "clickhouse-basics");

  if (loading) {
    return (
      <div className="p-4 sm:p-6">
        <Loading stage={QUERY_LOADING_STAGE_LABEL[queryLoadingPhase]} subtitle={null} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6">
        <div className="bg-white border border-neutral-border rounded-xl p-6 shadow-sm">
          <p className="text-neutral-text/70">Ошибка загрузки курсов: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title="Курсы по ClickHouse и SQL"
        description="Структурированные курсы по ClickHouse: от основ до продвинутых техник. Интерактивные уроки с практическими заданиями. Бесплатный курс ClickHouse Basics."
        keywords="курсы ClickHouse, обучение SQL онлайн, ClickHouse курсы бесплатно, SQL обучение с нуля, курсы баз данных, ClickHouse для аналитиков, Data Engineering курсы, Big Data обучение, ClickHouse tutorial, SQL практика, интерактивные курсы, онлайн обучение программированию, ClickHouse основы, продвинутый ClickHouse, ClickHouse оптимизация"
        structuredData={[
          generateBreadcrumbSchema([
            { name: "Главная", url: "/" },
            { name: "Курсы", url: "/courses" },
          ]),
        ]}
      />

      {/* ── Main scroll area — offset right by sidebar width on desktop ── */}
      <div
        ref={contentRef}
        className="p-4 sm:p-6"
        style={isDesktop && showAITutor ? { paddingRight: `calc(1.5rem + ${aiTutorResize.size}px)` } : undefined}
      >
        <div className="flex items-center justify-between mb-4 sm:mb-5">
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-neutral-text">Курсы</h1>

          <div className="flex items-center gap-1.5">
            {/* Show AI Tutor button on desktop when hidden */}
            {isDesktop && !showAITutor && (
              <button
                type="button"
                onClick={() => setShowAITutor(true)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-primary/10 text-primary border border-primary/20 text-sm font-medium hover:bg-primary/20 transition-colors"
              >
                <Sparkles className="w-4 h-4" />
                AI Tutor
              </button>
            )}

            {/* Mobile buttons */}
            <div className="lg:hidden flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => {
                  setMobileModalInitialMode("chat");
                  setMobileModalOpen(true);
                }}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-primary/10 text-primary border border-primary/20 text-sm font-medium hover:bg-primary/20 transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
                Чат
              </button>
              <button
                type="button"
                onClick={() => {
                  setMobileModalInitialMode("roadmap");
                  setMobileModalOpen(true);
                }}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white text-neutral-text border border-neutral-border text-sm font-medium hover:bg-primary/10 hover:border-primary/30 transition-colors"
              >
                <Map className="w-4 h-4" />
                Roadmap
              </button>
            </div>
          </div>
        </div>

        {/* Top row: до md — колонка; md–lg — ряд без равной высоты; lg+ — растягивание как раньше */}
        <div className="flex flex-col md:flex-row gap-4 mb-4 md:items-start lg:items-stretch">
          {/* Featured course card */}
          {basicCourse && (
            <div className="max-md:w-full min-w-0 md:flex-[6]">
              <CourseCard
                title={basicCourse.title}
                description={basicCourse.description || basicCourse.subtitle}
                backgroundPattern="bg-gradient-to-br from-yellow-400 via-orange-500 to-red-500"
                coverImage={basicCourse.coverImage}
                durationHours={basicCourse.durationHours}
                modulesCount={basicCourse.modules?.length}
                targetLevel={basicCourse.targetLevel}
                isFeatured={true}
                isFree={true}
                isInDev={basicCourse.slug === "clickhouse-basics"}
                onClick={() => navigate(`/courses/${basicCourse.slug}`)}
              />
            </div>
          )}

          {/* Simulator product card */}
          <button
            type="button"
            onClick={() => navigate("/simulator")}
            className="group max-md:w-full md:flex-[4] min-w-0 relative overflow-hidden bg-neutral-text rounded-2xl p-4 sm:p-6 text-left flex flex-col justify-between gap-3 md:gap-4 hover:shadow-xl transition-all duration-300"
          >
            {/* Background grid pattern */}
            <div
              className="absolute inset-0 opacity-[0.07]"
              style={{
                backgroundImage: `linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)`,
                backgroundSize: "28px 28px",
              }}
            />
            {/* Glow */}
            <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-primary/30 blur-3xl pointer-events-none" />

            <div className="relative z-10 flex flex-col md:h-full gap-3 md:gap-4">
              {/* Top */}
              <div>
                <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold bg-primary/20 text-primary border border-primary/30 mb-2 md:mb-3">
                  Практика
                </span>
                <h2 className="text-lg sm:text-xl font-bold text-white leading-snug mb-1.5 md:mb-2">
                  Тренажёр<br />SQL-запросов
                </h2>
                <p className="text-xs sm:text-sm text-white/60 leading-relaxed line-clamp-3 sm:line-clamp-none">
                  Реальные задачи по ClickHouse — логистика, e-commerce, продуктовая аналитика. С автопроверкой и подсказками.
                </p>
              </div>

              {/* Stats */}
              <div className="flex gap-3 sm:gap-4 flex-wrap">
                {[
                  { value: "50+", label: "задач" },
                  { value: "3", label: "датасета" },
                  { value: "SQL", label: "автопроверка" },
                ].map(({ value, label }) => (
                  <div key={label}>
                    <p className="text-white font-bold text-sm sm:text-base leading-none">{value}</p>
                    <p className="text-white/45 text-[10px] sm:text-[11px] mt-0.5">{label}</p>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className="flex items-center gap-2 mt-1 md:mt-auto">
                <span className="text-xs sm:text-sm font-semibold text-white group-hover:gap-3 transition-all">
                  Открыть тренажёр
                </span>
                <span className="text-white group-hover:translate-x-1 transition-transform duration-200">→</span>
              </div>
            </div>
          </button>
        </div>

        {/* All other courses — clean 3-column grid */}
        {(() => {
          const patterns = [
            "bg-gradient-to-br from-blue-400 via-blue-500 to-cyan-500",
            "bg-gradient-to-br from-purple-400 via-purple-500 to-indigo-500",
            "bg-gradient-to-br from-green-400 via-emerald-500 to-teal-500",
          ];
          const allCards = [
            ...otherCourses.map((course, i) => ({
              key: course.slug,
              title: course.title,
              description: course.description || course.subtitle,
              backgroundPattern: patterns[i % patterns.length],
              coverImage: course.coverImage,
              durationHours: course.durationHours,
              modulesCount: course.modules?.length,
              targetLevel: course.targetLevel,
              isDisabled: false,
              price: undefined as string | undefined,
              onClick: () => navigate(`/courses/${course.slug}`),
            })),
            ...IN_DEV_COURSES.map((course, i) => ({
              key: `in-dev-${i}`,
              title: course.title,
              description: course.description,
              backgroundPattern: course.backgroundPattern,
              coverImage: undefined as string | undefined,
              durationHours: course.durationHours,
              modulesCount: undefined as number | undefined,
              targetLevel: course.targetLevel,
              isDisabled: false,
              isInDev: true,
              price: undefined as string | undefined,
              onClick: () => navigate(`/courses/preview/${course.slug}`),
            })),
          ];

          return (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 items-stretch">
              {allCards.map(({ key, ...props }) => (
                <CourseCard key={key} {...props} />
              ))}

              {/* Corporate training card */}
              <button
                type="button"
                onClick={() => navigate("/corporate")}
                className="group relative h-full min-h-0 w-full overflow-hidden bg-neutral-800 rounded-2xl p-4 text-left flex flex-col hover:shadow-xl transition-all duration-300"
              >
                <div
                  className="absolute inset-0 opacity-[0.06]"
                  style={{
                    backgroundImage: `linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)`,
                    backgroundSize: "20px 20px",
                  }}
                />
                <div className="absolute -bottom-10 -right-10 w-36 h-36 rounded-full bg-teal-400/20 blur-2xl pointer-events-none" />

                <div className="relative z-10 flex min-h-0 flex-1 flex-col justify-between gap-2">
                  <div>
                    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-teal-400/20 text-teal-300 border border-teal-400/30 mb-2">
                      B2B
                    </span>
                    <h3 className="text-sm font-bold text-white leading-snug line-clamp-2">
                      Корпоративное обучение
                    </h3>
                    <p className="text-[11px] text-white/55 mt-1 leading-relaxed line-clamp-2">
                      Курсы под задачи команды, воркшопы и консультации по ClickHouse
                    </p>
                  </div>

                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-[11px] font-semibold text-teal-300">Подробнее</span>
                    <span className="text-teal-300 group-hover:translate-x-1 transition-transform duration-200 text-xs">→</span>
                  </div>
                </div>
              </button>
            </div>
          );
        })()}
      </div>

      {/* ── Desktop: resize handle ── */}
      {showAITutor && (
        <div
          ref={resizeHandleRef}
          onMouseDown={aiTutorResize.onMouseDown}
          onTouchStart={aiTutorResize.onTouchStart}
          className="hidden lg:flex fixed bottom-0 justify-center cursor-col-resize"
          style={{
            top: AI_TUTOR_DESKTOP_TOP,
            right: aiTutorResize.size,
            width: "6px",
            zIndex: LAYERS.aiTutorPanel + 1,
          }}
          aria-hidden
        >
          <div className="w-1 rounded bg-transparent hover:bg-primary/40 transition-colors h-full" />
        </div>
      )}

      {/* ── Desktop: fixed AI Tutor sidebar (right edge) ── */}
      {showAITutor && (
        <aside
          ref={aiTutorResize.elementRef}
          className="hidden lg:flex flex-col min-h-0 fixed right-0 bottom-0 border-l border-neutral-border bg-white overflow-hidden"
          style={{ top: AI_TUTOR_DESKTOP_TOP, width: aiTutorResize.size, zIndex: LAYERS.aiTutorPanel }}
        >
          <AITutorDualModePanel
            user={user}
            onClose={() => setShowAITutor(false)}
            embedded
            mode={tutorAsideMode}
            onModeChange={setTutorAsideMode}
            showCloseButton={true}
            onMinWidthChange={setChatPanelMinWidth}
            chatProps={{
              contextType: "generic",
              enableFeedback: true,
              embedded: true,
              headerSubtitle: "Вопросы по курсам и обучению.",
              showChatHistoryInEmbedded: true,
              onMinWidthChange: setChatPanelMinWidth,
              containerClassName: "flex-1 min-h-0 flex flex-col overflow-hidden border-0 shadow-none rounded-none",
            }}
            roadmapProps={{ isMobile: false }}
          />
        </aside>
      )}

      {/* Mobile modal */}
      {mobileModalOpen && (
        <MobileTutorModal
          user={user}
          initialMode={mobileModalInitialMode}
          onClose={() => setMobileModalOpen(false)}
        />
      )}

    </>
  );
};
